<template lang="html">
  <div v-if="benefitDetailData" class="benefits">
    <NavBar :title="benefitDetailData.venueTitle" back="back" commune="commune" />
    <Banner />
    <h2>{{ benefitDetailData.venueTitle }}</h2>
    <BenefitIframe v-if="benefitDetailData.iframeUrl" />
    <div v-else>
      <img class="hero" :src="getBenefitImageUrl(benefitDetailData)" />
      <div class="content">
        <div class="benefit-overview">
          <div class="benefit-option-list">
            <div class="benefit-option-favorite iconbutton" v-if="loggedInUser" @click="toggleFavorite(benefitId)">
              <font-awesome-icon icon="bookmark"></font-awesome-icon>
              <font-awesome-icon class="benefit-option-favorited" v-if="isFavorited" icon="star"></font-awesome-icon>
            </div>
          </div>
          <div class="benefit-title">{{ benefitDetailData.venueTitle }}</div>
          <div class="benefit-description">{{ benefitDetailData.benefitDescription }}</div>
          <div class="benefit-rating" v-if="benefitDetailData.venueGoogleRating">
            <font-awesome-icon class="benefit-rating-google" :icon="['fab', 'google']"></font-awesome-icon>
            <font-awesome-icon
              class="benefit-rating-star benefit-rating-full"
              v-for="n in Math.round(benefitDetailData.venueGoogleRating)"
              :key="'full-' + n"
              icon="star"
            ></font-awesome-icon>
            <font-awesome-icon
              class="benefit-rating-star benefit-rating-empty"
              v-for="n in 5 - Math.round(benefitDetailData.venueGoogleRating)"
              :key="'empty-' + n"
              icon="star"
            ></font-awesome-icon>
            <NewTranslation
              class="benefit-rating-amount"
              path="benefitsDetail.rating"
              :params="{ rating: benefitDetailData.venueGoogleRating }"
            ></NewTranslation>
          </div>
        </div>
        <div class="benefit-local" v-if="benefitDetailData.venueDescription">
          <NewTranslation class="benefit-headline" path="benefitsDetail.venueDescription"></NewTranslation>
          <div class="benefit-information">{{ benefitDetailData.venueDescription }}</div>
        </div>
        <div class="contact-list">
          <div class="contact-entry" v-if="benefitDetailData.venueLocation.length > 1">
            <div class="iconbutton icon-pin">
              <font-awesome-icon icon="map-pin"></font-awesome-icon>
            </div>
            <div class="contact-entry-text">{{ benefitDetailData.venueLocation }}</div>
          </div>
          <div class="contact-entry" v-if="benefitDetailData.venueWeb">
            <div class="iconbutton icon-globe">
              <font-awesome-icon icon="globe-americas"></font-awesome-icon>
            </div>
            <div class="contact-entry-text">{{ benefitDetailData.venueWeb }}</div>
          </div>
          <div class="contact-entry" v-if="benefitDetailData.venuePhone">
            <div class="iconbutton">
              <font-awesome-icon icon="phone-alt"></font-awesome-icon>
            </div>
            <div class="contact-entry-text">{{ benefitDetailData.venuePhone }}</div>
          </div>
          <div
            class="contact-entry"
            v-if="benefitDetailData.venueGoogleOpeningHour"
            @click="openingHoursOpen = !openingHoursOpen"
          >
            <div class="iconbutton">
              <font-awesome-icon icon="clock"></font-awesome-icon>
            </div>
            <div class="contact-entry-text">
              {{
                benefitDetailData.venueGoogleOpeningHour.open_now
                  ? translate('benefitsDetail.nowOpen.label')
                  : translate('benefitsDetail.nowClosed.label')
              }}
            </div>
            <div class="icon icon-toggle" :class="{ 'is-active': openingHoursOpen }">
              <font-awesome-icon icon="chevron-down"></font-awesome-icon>
            </div>
          </div>
        </div>
        <div class="openinghour" v-if="openingHoursOpen">
          <div
            class="openinghour-entry"
            v-for="(openinghour, i) in benefitDetailData.venueGoogleOpeningHour.weekday_text"
            :class="{ 'is-today': i === dateOfWeek }"
          >
            <div class="openinghour-singletext" v-if="openinghour.split(': ').length &lt; 2">{{ openinghour }}</div>
            <template v-else="v-else">
              <div class="openinghour-day">{{ openinghour.split(': ')[0] }}</div>
              <div class="openinghour-time">{{ openinghour.split(': ')[1] }}</div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div v-if="qualified && publicTransportBenefit">
      <Button
        color="green"
        center
        @click.native="openPublicTransportTicket()"
        path="benefitsDetail.coupon.publicTransportTicket.showTicket"
      >
        <font-awesome-icon icon="bus" slot="icon" />
      </Button>
    </div>
    <div class="coupon" v-else v-bind:class="getClassCoupon()">
      <span
        class="icon icon-info"
        @click="
          showInfo(translate('benefitsDetail.coupon.dialog.body'), translate('benefitsDetail.coupon.dialog.title'))
        "
        ><font-awesome-icon icon="info-circle"></font-awesome-icon
      ></span>
      <NewTranslation
        :path="`benefitsDetail.coupon.voidable.${benefitDetailData.benefitVoidable ? 'once' : 'multiple'}`"
        @click="openCoupon(benefitDetailData)"
      />
    </div>
    <TabBar />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import i18next from 'i18next';

import NavBar from '@/components/NavBar.vue';
import TabBar from '@/components/TabBar.vue';
import Banner from '@/components/Banner.vue';
import Button from '@/components/Button.vue';
import NewTranslation from '@/components/i18n/NewTranslation';
import BenefitIframe from '@/components/BenefitIframe.vue';
import RemoteAssets from '@/app/utils/RemoteAssets';

export default {
  components: {
    NewTranslation,
    NavBar,
    TabBar,
    Banner,
    BenefitIframe,
    Button,
  },
  data() {
    return {
      openingHoursOpen: false,
      dateOfWeek: 0,
      benefitId: parseInt(this.$route.query.benefitId, 10),
      qualified: false,
    };
  },
  mounted() {
    let dayStartingSunday = this.$moment().day();
    this.dateOfWeek = dayStartingSunday ? --dayStartingSunday : 6;
  },
  created() {
    this.userQualifiedBenefits().then(result => {
      this.qualified = result.qualified;
    });
  },
  methods: {
    translate(path, params) {
      return i18next.t(path, params);
    },
    openCoupon(benefit) {
      //const couponDisabled = this.benefitDetailData.benefitVoided || !this.qualified;
      //console.log(`benefitVoidingsCount: ${this.benefitDetailData.benefitVoidingsCount} maxAmountOfVoidings: ${this.benefitDetailData.maxAmountOfVoidings}`);
      const couponDisabled = this.benefitDetailData.benefitVoidingsCount >= this.benefitDetailData.maxAmountOfVoidings || !this.qualified;
      if (couponDisabled) return;
      this.$store.commit('openOverlay', {
        overlayName: 'CouponGuide',
        overlayData: {
          ...benefit,
        },
      });
    },
    showInfo(message, title) {
      this.$store.commit('openOverlay', {
        overlayName: 'Info',
        overlayData: {
          title,
          message,
        },
      });
    },
    toggleFavorite() {
      const benefit = this.$store.getters.benefitsDetail(this.benefitId);
      if (this.isFavorited) {
        this.$store.dispatch('removeFavorite', benefit);
      } else {
        this.$store.dispatch('addFavorite', benefit);
      }
    },
    getBenefitImageUrl(benefit) {
      return RemoteAssets.benefitImageUrl(benefit.venueImage);
    },
    getClassCoupon() {
      // console.log(`benefitVoidingsCount: ${this.benefitDetailData.benefitVoidingsCount} maxAmountOfVoidings: ${this.benefitDetailData.maxAmountOfVoidings}`);
      return {
        'is-valid': this.qualified,
        'is-invalid': !this.qualified || this.benefitDetailData.benefitVoidingsCount >= this.benefitDetailData.maxAmountOfVoidings,
      };
    },
    async openPublicTransportTicket() {
      this.$store.commit('openOverlay', {
        overlayName: 'PublicTransportTicket',
      });
    },
    ...mapActions(['userQualifiedBenefits']),
  },
  computed: {
    ...mapGetters(['favorites', 'loggedInUser']),
    isFavorited() {
      if (!this.favorites || this.favorites.length === 0) {
        return false;
      }
      return this.favorites.some(fav => fav.id === this.$store.getters.benefitsDetail(this.benefitId).id);
    },
    benefitDetailData() {
      return this.$store.getters.benefitsDetail(this.benefitId);
    },
    publicTransportBenefit() {
      // TODO: add boolean field for enabling public transport ticket
      return this.benefitId == 200;
    },
  },
};
</script>

<style lang="sass" scoped>
.benefits
  padding-bottom: 100px

.hero
  width: 100%
  height: 190px
  object-fit: cover
  object-position: center center

.content
  padding: 20px

.benefit
  &-overview
    position: relative
    padding-bottom: 20px
    border-bottom: 1px solid #F4F4F6

  &-option
    &-list
      position: absolute
      top: 0
      right: 0
      display: flex

    &-share
      padding: 7px

    &-favorite
      padding: 7px 8px
      position: relative

    &-favorited
      color: white
      position: absolute
      top: 8px
      left: 9px
      width: 12px

  &-title
    font-size: 1.25rem
    max-width: calc(100% - 90px)

  &-description
    margin-top: 5px
    max-width: calc(100% - 50px)

  &-rating
    display: flex
    margin: 10px 0 0

    &-google
      margin-right: 4px

    &-star
      height: 20px
      width: 14px
      object-fit: contain
    // margin: 0 3px
    &-full
      color: #D1701D

    &-empty
      color: #454F63

    &-amount
      font-size: 0.8rem
      color: #444
      margin-left: 5px

  &-local
    padding-bottom: 20px
    border-bottom: 1px solid #F4F4F6
    padding-top: 20px

  &-headline
    font-size: 1.25rem

  &-information
    margin-top: 6px

.contact
  &-list
    padding-top: 20px

  &-entry
    display: flex
    align-items: center
    margin: 10px 0

.openinghour
  display: flex
  flex-direction: column
  align-items: flex-end

  &-entry
    display: flex
    justify-content: space-between
    max-width: calc(100% - 42px)
    width: 100%
    margin-bottom: 3px
    font-size: .8rem

    &.is-today
      font-weight: bold

  // &-day

  // &-time

.icon-pin
  padding: 6px 8px

.icon-globe
  padding: 6px 8px

.icon
  width: 20px
  height: 20px
  display: inline-flex
  justify-content: center
  align-items: center
  border-radius: 20px
  border: 1px solid #1DB2D1

  svg
    color: #1DB2D1

  &-toggle
    margin-left: auto

    &.is-active
      transform: rotate(180deg)

    svg
      width: 10px

.coupon
  display: flex
  justify-content: center
  align-items: center
  height: 60px
  color: #fff
  text-decoration: underline
.is-valid
  background-color: #27D190
.is-invalid
  background-color: #DE3D36

.icon
  width: 20px
  height: 20px
  display: inline-flex
  justify-content: center
  align-items: center
  border-radius: 20px
  border: 1px solid #fff

  svg
    color: #fff

  &-info
    margin-right: 15px
    border: none

  &-toggle
    margin-left: auto

    svg
      width: 10px
</style>
